import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  clashByeHasScoreline,
  clashByeScorelineIsLive,
  clashHasScoreline,
  clashScorelineIsLive,
  clashWinnerSide,
  formatClashByeScoreline,
  formatClashScoreline,
  isClashBye
} from '../../utils/clashDisplay';
import {
  getAugmentDisplayName,
  getAugmentDisplaySvg,
  getAugmentShortDesc,
  parseAugmentRef
} from '../../utils/augments';
import { roundNumberFromRoundCode } from '../../utils/formatAchievementLeague';
import { formatRoundDisplay } from '../../utils/formatRoundDisplay';
import { clashWinnerIconSrc } from '../../utils/clashWinnerIcon';

const CLASH_AUGMENT_CHIP_COLOR = { sa: 'gold', bs: 'cyan', eif: 'green', gd: 'gold' };

function clashRoundLabel(roundCode) {
  const roundNumber = roundNumberFromRoundCode(roundCode);
  if (roundNumber == null) return null;
  return formatRoundDisplay(roundNumber);
}

function clashSectionTitle(roundCode) {
  const roundLabel = clashRoundLabel(roundCode);
  return roundLabel ? `Round ${roundLabel} clashes` : 'Weekly clashes';
}

function ClashAppliedAugmentChips({ augmentRefs }) {
  if (!Array.isArray(augmentRefs) || augmentRefs.length === 0) return null;

  return (
    <span className="pl-clash__augments" aria-label="Successfully applied clash plays">
      {augmentRefs.map((ref) => {
        const { code: base } = parseAugmentRef(ref);
        const color = CLASH_AUGMENT_CHIP_COLOR[base] || 'gold';
        const title = getAugmentShortDesc(ref) || getAugmentDisplayName(ref);
        const svg = getAugmentDisplaySvg(ref);
        return (
          <span
            key={ref}
            className={`pl-clash__aug-chip pl-clash__aug-chip--${color}`}
            title={title}
          >
            {svg ? (
              <img src={svg} alt={title} className="pl-clash__aug-chip-img" width={16} height={16} />
            ) : (
              <span className="pl-clash__aug-chip-text">{getAugmentDisplayName(ref)}</span>
            )}
          </span>
        );
      })}
    </span>
  );
}

function MatchupLink({ username, you, winner, winnerIcon, augmentRefs }) {
  if (!username) return null;
  return (
    <span className="pl-clash-player-wrap">
      <Link
        to={`/profile/${encodeURIComponent(username)}`}
        className={'pl-clash-player' + (you ? ' pl-clash-player--you' : '')}
      >
        {username}
      </Link>
      <ClashAppliedAugmentChips augmentRefs={augmentRefs} />
      {winner ? (
        <span className="pl-clash__crown" title="Clash winner" aria-label="Clash winner">
          <img src={clashWinnerIconSrc(winnerIcon)} alt="" width={14} height={14} />
        </span>
      ) : null}
    </span>
  );
}

function ClashScoreline({ homePoints, awayPoints, result, alreadyCalculated }) {
  const scoreline = formatClashScoreline(homePoints, awayPoints, result, alreadyCalculated);
  if (!scoreline) return null;

  const winnerSide = scoreline.isLive ? null : clashWinnerSide(result, alreadyCalculated);
  const tie = !scoreline.isLive && result === 't';
  const homeClass =
    'pl-clash__score-val' +
    (tie || winnerSide === 'h' ? ' pl-clash__score-val--emph' : '');
  const awayClass =
    'pl-clash__score-val' +
    (tie || winnerSide === 'a' ? ' pl-clash__score-val--emph' : '');
  const scoreLabel = scoreline.isLive
    ? `Current clash score ${scoreline.homePoints} to ${scoreline.awayPoints}`
    : `Clash score ${scoreline.homePoints} to ${scoreline.awayPoints}`;

  return (
    <span
      className={'pl-clash__score' + (scoreline.isLive ? ' pl-clash__score--live' : '')}
      aria-label={scoreLabel}
      title={scoreline.isLive ? 'Score updates as matches finish' : undefined}
    >
      <span className={homeClass}>{scoreline.homePoints}</span>
      <span className="pl-clash__score-sep" aria-hidden="true">
        –
      </span>
      <span className={awayClass}>{scoreline.awayPoints}</span>
    </span>
  );
}

function ClashByeScoreline({ homePoints, alreadyCalculated }) {
  const scoreline = formatClashByeScoreline(homePoints, alreadyCalculated);
  if (!scoreline) {
    return <span className="pl-clash__bye">bye</span>;
  }

  const scoreLabel = scoreline.isLive
    ? `Current match points ${scoreline.homePoints} (bye)`
    : `Match points ${scoreline.homePoints} (bye)`;

  return (
    <span
      className={'pl-clash__bye-score' + (scoreline.isLive ? ' pl-clash__bye-score--live' : '')}
      aria-label={scoreLabel}
      title={scoreline.isLive ? 'Points update as matches finish' : undefined}
    >
      <span className="pl-clash__score-val pl-clash__score-val--emph">{scoreline.homePoints}</span>
      <span className="pl-clash__bye-label">bye</span>
    </span>
  );
}

function ClashRoundBlock({ roundPayload, currentUsername, hideLabel = false }) {
  const { clashes, roundCode, alreadyCalculated } = roundPayload;
  if (!Array.isArray(clashes) || clashes.length === 0) return null;

  return (
    <section className="pl-clash-round" aria-label={clashSectionTitle(roundCode)}>
      {hideLabel ? null : (
        <h4 className="pl-clash__label">{clashSectionTitle(roundCode)}</h4>
      )}
      <ul className="pl-clash__list">
        {clashes.map((slot, index) => {
          const { homeUser, awayUser, result, homePoints, awayPoints, homeAugments, awayAugments, winnerIcon } =
            slot;
          const homeYou =
            currentUsername && String(homeUser).toLowerCase() === String(currentUsername).toLowerCase();
          const awayYou =
            !isClashBye(awayUser) &&
            currentUsername &&
            String(awayUser).toLowerCase() === String(currentUsername).toLowerCase();
          const bye = isClashBye(awayUser);
          const winnerSide = clashWinnerSide(result, alreadyCalculated);
          const showScoreline = clashHasScoreline(homePoints, awayPoints, alreadyCalculated);
          const scoreIsLive = clashScorelineIsLive(homePoints, awayPoints, alreadyCalculated);
          const showByeScoreline = bye && clashByeHasScoreline(homePoints);
          const byeScoreIsLive = bye && clashByeScorelineIsLive(homePoints, alreadyCalculated);

          return (
            <li
              key={`${homeUser}-${awayUser ?? 'bye'}-${index}`}
              className={
                'pl-clash__item' +
                (scoreIsLive || byeScoreIsLive ? ' pl-clash__item--live' : '')
              }
            >
              <div className="pl-clash__side pl-clash__side--home">
                <MatchupLink
                  username={homeUser}
                  you={homeYou}
                  winner={winnerSide === 'h'}
                  winnerIcon={winnerIcon}
                  augmentRefs={homeAugments}
                />
              </div>
              <div className="pl-clash__center">
                {bye ? (
                  showByeScoreline ? (
                    <ClashByeScoreline
                      homePoints={homePoints}
                      alreadyCalculated={alreadyCalculated}
                    />
                  ) : (
                    <span className="pl-clash__bye">bye</span>
                  )
                ) : showScoreline ? (
                  <ClashScoreline
                    homePoints={homePoints}
                    awayPoints={awayPoints}
                    result={result}
                    alreadyCalculated={alreadyCalculated}
                  />
                ) : (
                  <span className="pl-clash__vs" aria-hidden="true">
                    vs
                  </span>
                )}
              </div>
              <div className="pl-clash__side pl-clash__side--away">
                {bye ? null : (
                  <MatchupLink
                    username={awayUser}
                    you={awayYou}
                    winner={winnerSide === 'a'}
                    winnerIcon={winnerIcon}
                    augmentRefs={awayAugments}
                  />
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function defaultClashRoundIndex(rounds) {
  const currentIndex = rounds.findIndex((round) => round.isCurrent);
  if (currentIndex >= 0) return currentIndex;
  return Math.max(0, rounds.length - 1);
}

/**
 * Season league clash pairings for a private or tier league.
 * When multiple rounds are available, arrows step through one round at a time.
 */
export function LeagueClashFixtures({ clashRounds, currentUsername }) {
  const rounds = Array.isArray(clashRounds) ? clashRounds : [];
  const roundCodesKey = useMemo(
    () => rounds.map((round) => round.roundCode).join('|'),
    [rounds]
  );
  const [roundIndex, setRoundIndex] = useState(() => defaultClashRoundIndex(rounds));

  useEffect(() => {
    setRoundIndex(defaultClashRoundIndex(rounds));
  }, [roundCodesKey, rounds.length]);

  if (rounds.length === 0) return null;

  const safeIndex = Math.min(Math.max(roundIndex, 0), rounds.length - 1);
  const selectedRound = rounds[safeIndex];
  const hasMultipleRounds = rounds.length > 1;
  const canGoPrevious = safeIndex > 0;
  const canGoNext = safeIndex < rounds.length - 1;

  if (!selectedRound) return null;

  return (
    <div className="pl-clash">
      {hasMultipleRounds ? (
        <div className="pl-clash__nav">
          <button
            type="button"
            className="pl-clash__nav-btn"
            onClick={() => setRoundIndex((index) => Math.max(0, index - 1))}
            disabled={!canGoPrevious}
            aria-label="Previous round clashes"
          >
            ←
          </button>
          <h4 className="pl-clash__label pl-clash__label--nav">
            {clashSectionTitle(selectedRound.roundCode)}
          </h4>
          <button
            type="button"
            className="pl-clash__nav-btn"
            onClick={() =>
              setRoundIndex((index) => Math.min(rounds.length - 1, index + 1))
            }
            disabled={!canGoNext}
            aria-label="Next round clashes"
          >
            →
          </button>
        </div>
      ) : null}
      <ClashRoundBlock
        key={selectedRound.roundCode}
        roundPayload={selectedRound}
        currentUsername={currentUsername}
        hideLabel={hasMultipleRounds}
      />
    </div>
  );
}
